/**
 * OAuth Login Buttons
 * Design System v2 - Teal + Lime color palette
 * Google & Microsoft SSO buttons
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { oauthAPI, OAuthProvider } from '@/utils/api';
import { logger } from '@/utils/logger';

// Google Icon SVG
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" xmlns="http://www.w3.org/2000/svg">
    <g transform="matrix(1, 0, 0, 1, 0, 0)">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </g>
  </svg>
);

// Microsoft Icon SVG
const MicrosoftIcon = () => (
  <svg viewBox="0 0 23 23" width="20" height="20" xmlns="http://www.w3.org/2000/svg">
    <rect fill="#f25022" x="1" y="1" width="10" height="10"/>
    <rect fill="#00a4ef" x="1" y="12" width="10" height="10"/>
    <rect fill="#7fba00" x="12" y="1" width="10" height="10"/>
    <rect fill="#ffb900" x="12" y="12" width="10" height="10"/>
  </svg>
);

interface OAuthButtonProps {
  provider: 'google' | 'microsoft';
  mode?: 'login' | 'link';
  disabled?: boolean;
  className?: string;
}

export const OAuthButton: React.FC<OAuthButtonProps> = ({
  provider,
  mode = 'login',
  disabled = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const handleClick = () => {
    setLoading(true);
    logger.log(`Initiating OAuth ${mode} with ${provider}`);

    if (mode === 'link') {
      oauthAPI.initiateLink(provider);
    } else {
      oauthAPI.initiateLogin(provider);
    }
  };

  const providerConfig = {
    google: {
      icon: GoogleIcon,
      label: 'Google',
      bgColor: 'bg-white hover:bg-gray-50',
      textColor: 'text-gray-700',
      borderColor: 'border-gray-300 hover:border-gray-400',
    },
    microsoft: {
      icon: MicrosoftIcon,
      label: 'Microsoft',
      bgColor: 'bg-white hover:bg-gray-50',
      textColor: 'text-gray-700',
      borderColor: 'border-gray-300 hover:border-gray-400',
    },
  };

  const config = providerConfig[provider];
  const Icon = config.icon;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || loading}
      className={`
        w-full flex items-center justify-center gap-3 py-3.5 px-4
        ${config.bgColor} ${config.textColor}
        border-2 ${config.borderColor}
        rounded-2xl font-semibold text-sm
        transition-all duration-300
        disabled:opacity-50 disabled:cursor-not-allowed
        hover:shadow-md hover:-translate-y-0.5
        active:scale-[0.98]
        ${className}
      `}
    >
      {loading ? (
        <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-400 border-t-transparent" />
      ) : (
        <Icon />
      )}
      <span>
        {mode === 'link' ? t('auth.oauth.linkWith', { provider: config.label }) : t('auth.oauth.continueWith', { provider: config.label })}
      </span>
    </button>
  );
};

interface OAuthButtonsGroupProps {
  mode?: 'login' | 'link';
  onlyEnabled?: boolean;
  showAlways?: boolean; // Show buttons even if providers aren't configured
}

export const OAuthButtonsGroup: React.FC<OAuthButtonsGroupProps> = ({
  mode = 'login',
  onlyEnabled = true,
  showAlways = true, // Default to always show for better UX
}) => {
  const { t } = useTranslation();
  const [providers, setProviders] = useState<OAuthProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const data = await oauthAPI.getProviders();
        setProviders(data);
        logger.log('OAuth providers loaded:', data);
      } catch (err) {
        logger.error('Failed to fetch OAuth providers:', err);
        setError(t('auth.oauth.loadError'));
        // If API fails, assume providers exist but aren't enabled
        if (showAlways) {
          setProviders([
            { provider: 'GOOGLE', enabled: false },
            { provider: 'MICROSOFT', enabled: false },
          ]);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProviders();
  }, [showAlways]);

  if (loading) {
    return (
      <div className="space-y-3">
        <div className="h-14 bg-gray-100 rounded-2xl animate-pulse" />
        <div className="h-14 bg-gray-100 rounded-2xl animate-pulse" />
      </div>
    );
  }

  // If showAlways, display buttons even if not enabled (but disabled)
  if (showAlways) {
    const googleEnabled = providers.some((p) => p.provider === 'GOOGLE' && p.enabled);
    const microsoftEnabled = providers.some((p) => p.provider === 'MICROSOFT' && p.enabled);

    return (
      <div className="space-y-3">
        <OAuthButton
          provider="google"
          mode={mode}
          disabled={!googleEnabled}
        />
        <OAuthButton
          provider="microsoft"
          mode={mode}
          disabled={!microsoftEnabled}
        />
        {(!googleEnabled && !microsoftEnabled) && (
          <p className="text-xs text-center text-gray-400 mt-2">
            {t('auth.oauth.comingSoon')}
          </p>
        )}
      </div>
    );
  }

  // Original behavior if showAlways is false
  if (error) {
    return null;
  }

  const enabledProviders = onlyEnabled
    ? providers.filter((p) => p.enabled)
    : providers;

  if (enabledProviders.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      {enabledProviders.some((p) => p.provider === 'GOOGLE') && (
        <OAuthButton provider="google" mode={mode} />
      )}
      {enabledProviders.some((p) => p.provider === 'MICROSOFT') && (
        <OAuthButton provider="microsoft" mode={mode} />
      )}
    </div>
  );
};

export default OAuthButtonsGroup;
